/**
 * 和とん わくわくポイントショップ - 顧客画面
 * ポイントを景品と交換できる楽しいショッピング体験
 */

import React, { useState, useEffect } from 'react';
import { Gift, Ticket, Package, Star, ShoppingBag, Info, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import apiClient from '../../utils/apiClient';

interface Prize {
  id: number;
  name: string;
  description: string;
  category: 'coupon' | 'goods' | 'special';
  points: number;
  stock: number;
  image_url?: string;
  icon?: string;
  exchange_rate?: number;
  limited?: boolean;
  deadline?: string;
}

interface UserInfo {
  line_user_id: string;
  name: string;
  points: number;
  rank: string;
  total_earned: number;
}

interface PointShopCustomerProps {
  lineUserId?: string;
  isDemo?: boolean;
}

const PointShopCustomer: React.FC<PointShopCustomerProps> = ({
  lineUserId = 'demo_user',
  isDemo = false
}) => {
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'coupon' | 'goods' | 'special'>('all');
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exchangeResult, setExchangeResult] = useState<any>(null);

  // カテゴリ設定
  const categories = [
    { id: 'all', name: '🎁 すべて', color: 'bg-blue-500' },
    { id: 'coupon', name: '🎫 クーポン', color: 'bg-purple-500' },
    { id: 'goods', name: '📦 景品', color: 'bg-green-500' },
    { id: 'special', name: '⭐ 限定品', color: 'bg-yellow-500' }
  ];

  useEffect(() => {
    loadData();
  }, [lineUserId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // デモモードまたはlineUserIdが指定されていない場合
      if (isDemo || !lineUserId || lineUserId === 'demo_user') {
        setUserInfo({
          line_user_id: 'demo_user',
          name: 'ゲスト',
          points: 0,
          rank: 'ブロンズ',
          total_earned: 0
        });

        // デモモードでも実際のAPIから景品データを取得
        const prizesRes = await apiClient.get('/point-shop/prizes?available_only=true');
        setPrizes(prizesRes.data.map((p: any) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          category: p.category === 'coupon' ? 'coupon' :
                   p.category === 'goods' ? 'goods' : 'special',
          points: p.points,
          stock: p.stock,
          image_url: p.image_url,
          icon: p.icon,
          limited: p.category === 'special'
        })));
      } else {
        // 実際のAPI呼び出し
        const [userRes, prizesRes] = await Promise.all([
          apiClient.get(`/points/member/${lineUserId}`),
          apiClient.get('/point-shop/prizes?available_only=true')
        ]);

        // ユーザー情報
        setUserInfo({
          line_user_id: lineUserId,
          name: userRes.data.name || 'お客様',
          points: userRes.data.points || userRes.data.current_points || 0,
          rank: userRes.data.current_rank || 'ブロンズ',
          total_earned: userRes.data.total_earned || 0
        });

        // 景品情報を整形
        setPrizes(prizesRes.data.map((p: any) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          category: p.category === 'coupon' ? 'coupon' :
                   p.category === 'goods' ? 'goods' : 'special',
          points: p.points,
          stock: p.stock,
          image_url: p.image_url,
          icon: p.icon,
          limited: p.category === 'special'
        })));
      }
    } catch (error) {
      console.error('データ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExchange = async () => {
    if (!selectedPrize || !userInfo) return;

    setLoading(true);
    try {
      if (isDemo) {
        // デモモード
        setTimeout(() => {
          setExchangeResult({
            success: true,
            message: '交換完了！',
            exchange_code: 'DEMO-' + Date.now(),
            remaining_points: userInfo.points - selectedPrize.points
          });
          setUserInfo({
            ...userInfo,
            points: userInfo.points - selectedPrize.points
          });
          setShowConfirm(false);
          setLoading(false);
        }, 1000);
      } else {
        // 実際のAPI呼び出し
        const response = await apiClient.post('/point-shop/exchange', {
          line_user_id: lineUserId,
          prize_id: selectedPrize.id
        });

        setExchangeResult(response.data);
        setUserInfo({
          ...userInfo,
          points: response.data.remaining_points
        });
        setShowConfirm(false);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || '交換に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrizes = selectedCategory === 'all'
    ? prizes
    : prizes.filter(p => p.category === selectedCategory);

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'プラチナ': return 'bg-gradient-to-r from-purple-400 to-purple-600';
      case 'ゴールド': return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
      case 'シルバー': return 'bg-gradient-to-r from-gray-300 to-gray-500';
      default: return 'bg-gradient-to-r from-orange-400 to-orange-600';
    }
  };

  if (loading && !userInfo) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-2xl">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-orange-50">
      {/* ヘッダー */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">
            🎁 和とん わくわくポイントショップ
          </h1>
          <p className="text-lg opacity-90">
            貯めたポイントで素敵な景品と交換しよう！
          </p>
        </div>
      </div>

      {/* ユーザー情報 */}
      {userInfo && (
        <div className="max-w-6xl mx-auto p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-2xl font-bold mb-2">
                  {userInfo.name}様
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`px-3 py-1 rounded-full text-white text-sm font-bold ${getRankColor(userInfo.rank)}`}>
                    {userInfo.rank}会員
                  </span>
                  <span className="text-gray-600">
                    累計獲得: {userInfo.total_earned}pt
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-gray-600 text-sm">現在のポイント</div>
                <div className="text-4xl font-bold text-orange-600">
                  {userInfo.points}
                  <span className="text-xl text-gray-600 ml-1">pt</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* カテゴリフィルター */}
      <div className="max-w-6xl mx-auto px-4 mb-6">
        <div className="flex space-x-2 overflow-x-auto">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id as any)}
              className={`px-6 py-3 rounded-lg font-bold whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? `${cat.color} text-white shadow-lg scale-105`
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* 景品一覧 */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrizes.map(prize => (
            <div
              key={prize.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:scale-105"
            >
              {/* 景品画像/アイコン */}
              <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
                {prize.limited && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold z-10">
                    限定
                  </div>
                )}
                {prize.deadline && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold z-10">
                    期限あり
                  </div>
                )}
                {prize.image_url ? (
                  <img
                    src={prize.image_url}
                    alt={prize.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // 画像読み込みエラー時はアイコンを表示
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`text-6xl ${prize.image_url ? 'hidden' : ''}`}>
                  {prize.icon || '🎁'}
                </div>
              </div>

              {/* 景品情報 */}
              <div className="p-4">
                <h3 className="text-xl font-bold mb-2">{prize.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{prize.description}</p>

                <div className="flex justify-between items-center mb-4">
                  <div>
                    <div className="text-2xl font-bold text-orange-600">
                      {prize.points}pt
                    </div>
                    {prize.exchange_rate && (
                      <div className="text-xs text-green-600">
                        {Math.round((1 - prize.exchange_rate) * 100)}%お得！
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">在庫</div>
                    <div className={`font-bold ${prize.stock < 10 ? 'text-red-600' : 'text-gray-700'}`}>
                      {prize.stock === 999 ? '∞' : `残り${prize.stock}`}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedPrize(prize);
                    setShowConfirm(true);
                  }}
                  disabled={!userInfo || userInfo.points < prize.points || prize.stock === 0}
                  className={`w-full py-3 rounded-lg font-bold transition-all ${
                    !userInfo || userInfo.points < prize.points || prize.stock === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-lg active:scale-95'
                  }`}
                >
                  {prize.stock === 0 ? '在庫切れ' :
                   !userInfo || userInfo.points < prize.points ? 'ポイント不足' :
                   '交換する'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 確認ダイアログ */}
      {showConfirm && selectedPrize && userInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4">交換確認</h3>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center text-6xl mb-4">
                {selectedPrize.icon || '🎁'}
              </div>
              <div className="text-center">
                <div className="text-xl font-bold mb-2">{selectedPrize.name}</div>
                <div className="text-gray-600">{selectedPrize.description}</div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span>必要ポイント</span>
                <span className="font-bold text-orange-600">
                  {selectedPrize.points}pt
                </span>
              </div>
              <div className="flex justify-between">
                <span>現在のポイント</span>
                <span className="font-bold">{userInfo.points}pt</span>
              </div>
              <div className="flex justify-between border-t pt-3">
                <span>交換後のポイント</span>
                <span className="font-bold text-xl">
                  {userInfo.points - selectedPrize.points}pt
                </span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setSelectedPrize(null);
                }}
                className="flex-1 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400"
                disabled={loading}
              >
                キャンセル
              </button>
              <button
                onClick={handleExchange}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-bold hover:shadow-lg disabled:opacity-50"
              >
                {loading ? '処理中...' : '交換する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 交換完了 */}
      {exchangeResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold mb-4">交換完了！</h3>
              <p className="text-gray-600 mb-6">
                景品の交換が完了しました。
              </p>

              {/* PIN番号の大きな表示 */}
              {exchangeResult.personal_pin && (
                <div className="mb-4 p-4 bg-gradient-to-r from-orange-100 to-red-100 rounded-xl">
                  <div className="text-sm text-gray-600 mb-2">店舗でお伝えください</div>
                  <div className="text-4xl font-bold text-orange-600 tracking-wider">
                    PIN: {exchangeResult.personal_pin}
                  </div>
                  {exchangeResult.pin_tier && (
                    <div className="text-xs text-gray-500 mt-2">
                      {exchangeResult.pin_tier}会員様専用番号
                    </div>
                  )}
                </div>
              )}

              {/* 交換コード（参考情報） */}
              {exchangeResult.exchange_code && (
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <div className="text-xs text-gray-500">交換コード（控え）</div>
                  <div className="text-sm font-mono">
                    {exchangeResult.exchange_code}
                  </div>
                </div>
              )}
              <button
                onClick={() => {
                  setExchangeResult(null);
                  loadData(); // リロード
                }}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-bold"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointShopCustomer;